import React from 'react';
import { CustomModal } from '../../../../common/CustomModal';
import { Plus, Trash2, Upload, Calendar, MapPin } from 'lucide-react';
import '../../../../../styles/components/auction-client-pay.scss';
import { createSiteVisitProject } from '../services/siteVisitProjectService';
import { toast } from 'react-toastify';

export class AddSiteVisitProject extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      project_name: '',
      description: '',
      is_active: true,
      image: null,
      start_at: '',
      end_at: '',
      places: [
        {
          place_name: '',
          address: ''
        }
      ],
      loading: false,
      submitting: false
    };
  }

  handleCancel = () => {
    this.props.onClose();
  };

  handleSubmit = async (e) => {
    e.preventDefault();
    this.setState({ loading: true, submitting: true });

    // Validate required fields
    if (!this.state.project_name || !this.state.start_at || !this.state.end_at) {
      toast.error('Please fill all required fields');
      this.setState({ loading: false, submitting: false });
      return;
    }

    // Validate places
    const validPlaces = this.state.places.every(place =>
      place.place_name && place.address
    );

    if (!validPlaces) {
      toast.error('Please fill all place fields');
      this.setState({ loading: false, submitting: false });
      return;
    }

    // Prepare form data
    const formData = new FormData();
    formData.append('project_name', this.state.project_name);
    formData.append('description', this.state.description);
    formData.append('is_active', this.state.is_active ? '1' : '0');
    formData.append('start_at', this.state.start_at);
    formData.append('end_at', this.state.end_at);

    if (this.state.image) {
      formData.append('image', this.state.image);
    }

    // Add places as array - append each place individually
    this.state.places.forEach((place, index) => {
      formData.append(`places[${index}][place_name]`, place.place_name);
      formData.append(`places[${index}][address]`, place.address);
    });

    // Alternative approach if the above doesn't work:
    // formData.append('places', JSON.stringify(this.state.places));

    console.log('Form Data:', {
      project_name: this.state.project_name,
      description: this.state.description,
      is_active: this.state.is_active,
      image: this.state.image?.name,
      start_at: this.state.start_at,
      end_at: this.state.end_at,
      places: this.state.places
    });

    try {
      const response = await createSiteVisitProject(formData, true);

      if (response.status) {
        toast.success(response.message || 'Project created successfully');
        this.props.onPaymentComplete?.();
        this.handleCancel();
        // Call success callback if provided
        if (this.props.onSuccess) {
          this.props.onSuccess();
        }
      } else {
        toast.error(response.message || 'Project creation failed');
      }
    } catch (error) {
      console.error('Error creating project:', error);
      toast.error('Something went wrong');
    } finally {
      this.setState({ loading: false, submitting: false });
    }
  };

  handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    this.setState({
      [name]: type === 'checkbox' ? checked : value
    });
  };

  handlePlaceChange = (index, field, value) => {
    const updatedPlaces = [...this.state.places];
    updatedPlaces[index][field] = value;
    this.setState({ places: updatedPlaces });
  };

  addPlace = () => {
    this.setState({
      places: [
        ...this.state.places,
        { place_name: '', address: '' }
      ]
    });
  };

  removePlace = (index) => {
    if (this.state.places.length > 1) {
      const updatedPlaces = this.state.places.filter((_, i) => i !== index);
      this.setState({ places: updatedPlaces });
    }
  };

  handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        toast.error('Please select a valid image file');
        return;
      }

      // Validate file size (e.g., 5MB max)
      if (file.size > 5 * 1024 * 1024) {
        toast.error('Image size should be less than 5MB');
        return;
      }

      this.setState({ image: file });
    }
  };

  render() {
    const { show } = this.props;
    const {
      project_name,
      description,
      is_active,
      image,
      start_at,
      end_at,
      places,
      loading,
      submitting
    } = this.state;

    return (
      <CustomModal
        isOpen={show}
        header="Add Site Visit Project"
        onClose={this.handleCancel}
        size="lg"
      >
        <div className="site-visit-project-form">
          <form onSubmit={this.handleSubmit}>
            {/* Project Basic Information */}
            <div className="form-section">
              <h3 className="section-title">Project Information</h3>

              <div className="form-group">
                <label className="form-label">Project Name *</label>
                <input
                  type="text"
                  name="project_name"
                  value={project_name}
                  onChange={this.handleInputChange}
                  className="form-input"
                  placeholder="Enter project name"
                  required
                  disabled={submitting}
                />
              </div>

              <div className="form-group">
                <label className="form-label">Description</label>
                <textarea
                  name="description"
                  value={description}
                  onChange={this.handleInputChange}
                  className="form-input"
                  placeholder="Enter project description"
                  rows="3"
                  disabled={submitting}
                />
              </div>

              <div className="form-group">
                <label className="form-label">Project Image</label>
                <div className="file-upload">
                  <input
                    type="file"
                    onChange={this.handleFileUpload}
                    accept="image/*"
                    className="file-input"
                    id="project-image"
                    disabled={submitting}
                  />
                  <label htmlFor="project-image" className="file-label">
                    <Upload className="icon" />
                    {image ? image.name : 'Choose Project Image'}
                  </label>
                </div>
                {image && (
                  <div className="file-preview">
                    <small>Selected: {image.name}</small>
                  </div>
                )}
              </div>

              <div className="form-group checkbox-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    name="is_active"
                    checked={is_active}
                    onChange={this.handleInputChange}
                    className="checkbox-input"
                    disabled={submitting}
                    hidden
                  />
                  <span className="checkmark"></span>
                  Active Project
                </label>
              </div>
            </div>

            {/* Date & Time Section */}
            <div className="form-section">
              <h3 className="section-title">Schedule</h3>
              <div className="row">
                <div className="col-md-6">
                  <div className="form-group">
                    <label className="form-label">Start Date & Time *</label>
                    <div className="input-with-icon">
                      <Calendar className="input-icon" size={16} />
                      <input
                        type="datetime-local"
                        name="start_at"
                        value={start_at}
                        onChange={this.handleInputChange}
                        className="form-input"
                        required
                        disabled={submitting}
                      />
                    </div>
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="form-group">
                    <label className="form-label">End Date & Time *</label>
                    <div className="input-with-icon">
                      <Calendar className="input-icon" size={16} />
                      <input
                        type="datetime-local"
                        name="end_at"
                        value={end_at}
                        onChange={this.handleInputChange}
                        className="form-input"
                        required
                        disabled={submitting}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Places Section */}
            <div className="form-section">
              <div className="section-header">
                <h3 className="section-title">Site Visit Places</h3>
              </div>

              {places.map((place, index) => (
                <div key={index} className="place-item">
                  <div className="place-header">
                    <h4>Area {index + 1}</h4>
                    {places.length > 1 && (
                      <button
                        type="button"
                        className="btn-remove-place"
                        onClick={() => this.removePlace(index)}
                        disabled={submitting}
                      >
                        <Trash2 size={16} />
                      </button>
                    )}
                  </div>

                  <div className="row">
                    <div className="col-md-6">
                      <div className="form-group">
                        <label className="form-label">Place Name *</label>
                        <div className="input-with-icon">
                          <MapPin className="input-icon" size={16} />
                          <input
                            type="text"
                            value={place.place_name}
                            onChange={(e) => this.handlePlaceChange(index, 'place_name', e.target.value)}
                            className="form-input"
                            placeholder="Enter place name"
                            required
                            disabled={submitting}
                          />
                        </div>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="form-group">
                        <label className="form-label">Address *</label>
                        <input
                          type="text"
                          value={place.address}
                          onChange={(e) => this.handlePlaceChange(index, 'address', e.target.value)}
                          className="form-input"
                          placeholder="Enter address"
                          required
                          disabled={submitting}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <div className="section-header">

              <button
                type="button"
                className="btn-add-place"
                onClick={this.addPlace}
                disabled={submitting}
              >
                <Plus size={16} />
                Add Place
              </button>
            </div>

            {/* Form Actions */}
            <div className="form-actions">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={this.handleCancel}
                disabled={submitting}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={submitting || loading}
              >
                {submitting ? 'Creating...' : 'Create Project'}
              </button>
            </div>
          </form>
        </div>
      </CustomModal>
    );
  }
}